'use client';

import * as React from 'react';
import { cn } from '../utils';
import type { BookId } from '@sharpr/types';
import { presentationFor } from './books';
import { useBook } from './bookCatalog';

export interface SportsbookLogoProps {
  book: BookId;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showText?: boolean;
}

export const SportsbookLogo: React.FC<SportsbookLogoProps> = ({
  book: bookId,
  size = 'md',
  className,
  showText = false,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [isDark, setIsDark] = React.useState(false);
  
  // Check for dark mode
  React.useEffect(() => {
    // Check if dark class is on document element
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  
  // Bigger sizes for actual logos - wider rectangles
  const logoSizeClasses = {
    xs: 'h-6 w-12',
    sm: 'h-9 w-20',
    md: 'h-14 w-28',
    lg: 'h-20 w-36',
  };

  // Original sizes for branded badges
  const badgeSizeClasses = {
    xs: 'h-5 w-5',
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  const textSizeClasses = {
    xs: 'text-[8px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
  };

  const { name } = useBook(bookId);
  const presentation = presentationFor(bookId, name);
  const colors = presentation.badge;
  const abbreviation = presentation.abbreviation;

  // Use dark logo if in dark mode and it exists, otherwise fallback to regular logo
  const logoUrl = isDark && presentation.darkLogo ? presentation.darkLogo : presentation.logo;
  const hasLogo = !!logoUrl && !imageError;

  if (!hasLogo) {
    // Fallback to styled abbreviation with text for sportsbooks without logos
    return (
      <div className={cn('flex flex-col items-center gap-1', className)}>
        <div
          className={cn(
            'flex items-center justify-center rounded-lg font-bold',
            badgeSizeClasses[size],
            colors.bg,
            colors.text,
            'shadow-sm border border-white/10'
          )}
        >
          <span className={textSizeClasses[size]}>{abbreviation}</span>
        </div>
        {showText && (
          <span className="text-[10px] font-medium text-muted-foreground">
            {name}
          </span>
        )}
      </div>
    );
  }
  
  // For sportsbooks with logos, show only the logo (no text unless explicitly requested)
  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className={cn(logoSizeClasses[size], 'object-contain', presentation.logoScale)}
        onError={() => setImageError(true)}
        loading="lazy"
      />
      {showText && (
        <span className="text-[10px] font-medium text-muted-foreground">
          {name}
        </span>
      )}
    </div>
  );
};